import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CalendlyEmbed } from "@/components/CalendlyEmbed";
import { SEO } from "@/components/SEO";
import { NavLink } from "@/components/NavLink";
import { Helmet } from "react-helmet-async";
import { CheckCircle, Phone, Users, Award, Clock, Shield, Target, Zap } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const LawnCareWebDesignAgency = () => {
  const faqs = [
    {
      question: "What makes a lawn care web design agency different from a general agency?",
      answer: "A lawn care web design agency understands seasonal business patterns, service-based conversions, and local SEO for lawn care specifically. General agencies build generic sites that don't address industry-specific needs like quote forms, service area pages, and before/after galleries."
    },
    {
      question: "How much does a lawn care web design agency charge?",
      answer: "Agency pricing varies widely—from $2,000–$10,000+ for one-time builds to $200–$500/month for subscription models. We offer $0 upfront with $200–$300/month subscriptions that include ongoing maintenance and support."
    },
    {
      question: "Should I hire a freelancer or an agency for my lawn care website?",
      answer: "Agencies typically offer more reliability, ongoing support, and structured processes. Freelancers may be cheaper but can disappear or lack capacity. A niche-focused agency like ours combines the personal attention of a freelancer with agency-level systems."
    },
    {
      question: "How long does it take for an agency to build my lawn care website?",
      answer: "Most agencies take 4–8 weeks. We typically deliver lawn care websites in 10–14 days because we specialize exclusively in this niche and have proven templates and processes."
    },
    {
      question: "Do I need to provide content to the agency?",
      answer: "You'll need to provide photos of your work (phone photos are fine), your service list, and service areas. A good agency handles the rest—copywriting, layout, SEO structure, and design."
    },
    {
      question: "Will the agency help with SEO and not just design?",
      answer: "A quality lawn care web design agency builds SEO into the foundation—proper headings, meta tags, schema markup, service area pages, and mobile optimization. Ask about on-page SEO before hiring any agency."
    },
    {
      question: "What should I look for when hiring a lawn care web design agency?",
      answer: "Look for: niche experience (lawn care/landscaping), portfolio examples in your industry, clear pricing, ongoing support options, and testimonials from similar businesses. Avoid agencies that can't show relevant work."
    },
    {
      question: "Can an agency help me rank in Google Maps?",
      answer: "Website agencies build the foundation (NAP consistency, service area pages, schema), but Google Business Profile optimization is often a separate service. We offer GBP optimization as an add-on after your website is live."
    },
    {
      question: "What happens after my website launches with an agency?",
      answer: "Good agencies offer ongoing support—content updates, maintenance, and performance monitoring. With our subscription model, you get unlimited reasonable edits, hosting, and security included monthly."
    },
    {
      question: "How do I know if an agency is right for my lawn care business?",
      answer: "Book a discovery call. A good agency will ask about your services, target areas, and goals—not just pitch packages. If they don't understand lawn care terminology and business model, they're not the right fit."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": "https://www.leafrushmarketing.com/lawn-care-web-design-agency/#faq",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://www.leafrushmarketing.com/#organization",
    "name": "Leaf Rush Marketing",
    "url": "https://www.leafrushmarketing.com",
    "logo": "https://www.leafrushmarketing.com/og-image.jpg",
    "description": "Lawn care web design agency specializing in websites for lawn care and landscaping companies."
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": "https://www.leafrushmarketing.com/lawn-care-web-design-agency/#webpage",
    "name": "Lawn Care Web Design Agency | Leaf Rush Marketing",
    "url": "https://www.leafrushmarketing.com/lawn-care-web-design-agency/",
    "description": "Niche-focused lawn care web design agency. We build websites exclusively for lawn care companies. $0 upfront, $200–$300/mo subscription.",
    "isPartOf": {
      "@id": "https://www.leafrushmarketing.com/#organization"
    }
  };

  return (
    <>
      <SEO 
        title="Lawn Care Web Design Agency | Leaf Rush"
        description="Niche-focused lawn care web design agency. We build websites exclusively for lawn care companies. $0 upfront, $200–$300/mo subscription."
        keywords="lawn care web design agency, lawn care website agency, lawn care digital agency, lawn care website company"
        canonical="https://www.leafrushmarketing.com/lawn-care-web-design-agency/"
        ogUrl="https://www.leafrushmarketing.com/lawn-care-web-design-agency/"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>
      </Helmet>

      <div className="flex flex-col">
        {/* Hero Section */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="container px-4 md:px-6 relative z-10">
            <div className="mx-auto max-w-4xl text-center space-y-6">
              <Badge variant="secondary" className="mb-4">
                Niche-Focused Agency
              </Badge>
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight">
                Why Choose a Niche Lawn Care Web Design Agency?
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
                Exploring whether to hire an agency for your lawn care website? Learn what separates niche-focused agencies from generalist shops—and why industry expertise matters.
              </p>
              
              {/* Quick Answer Box */}
              <Card className="bg-primary/5 border-primary/20 text-left max-w-2xl mx-auto">
                <CardContent className="pt-6">
                  <p className="font-semibold text-primary mb-2">Quick Answer:</p>
                  <p className="text-muted-foreground">
                    A lawn care web design agency specializes in building websites for lawn care businesses. Unlike general agencies, we understand seasonal patterns, service-based conversions, and local SEO specific to lawn care. Expect to pay $200–$500/month or $2,000–$10,000 one-time.
                  </p>
                </CardContent>
              </Card>

              {/* Not Sure Callout */}
              <div className="bg-accent/30 rounded-lg p-4 text-sm text-muted-foreground max-w-2xl mx-auto">
                <strong>Not sure if this is what you need?</strong> If you're looking to understand all your options for a new site, visit our main{" "}
                <NavLink to="/lawn-care-website-design/" className="text-primary underline hover:no-underline">
                  lawn care website design
                </NavLink>{" "}
                page for a complete overview.
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose a Niche Agency */}
        <section className="py-12 md:py-16 bg-accent/20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                Why Choose a Lawn Care-Focused Agency?
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex gap-3">
                  <Target className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold">Industry-Specific Expertise</p>
                    <p className="text-sm text-muted-foreground">
                      We know lawn care terminology, seasonal patterns, and what homeowners look for when hiring. No learning curve.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Zap className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold">Faster Delivery</p>
                    <p className="text-sm text-muted-foreground">
                      Proven templates and processes mean we deliver in weeks, not months. Most sites are live in 10–14 days.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Award className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold">Conversion-Optimized</p>
                    <p className="text-sm text-muted-foreground">
                      Every element is designed to convert visitors into quote requests and phone calls—not just look pretty.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Shield className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold">Ongoing Support</p>
                    <p className="text-sm text-muted-foreground">
                      Subscription model means you always have support for updates, questions, and seasonal changes.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* What We Build */}
        <section className="py-12 md:py-16">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                What Our Agency Builds for Lawn Care Companies
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Complete Websites</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      7–14 page sites with home, services, service areas, about, reviews, contact, and FAQ pages.
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Service Pages</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      Dedicated pages for mowing, fertilization, aeration, cleanups, and every service you offer.
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Local SEO Structure</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      Service area pages, proper schema markup, and on-page optimization to rank where you work.
                    </p>
                  </CardContent>
                </Card>
              </div>
              <div className="text-center mt-8">
                <p className="text-muted-foreground mb-4">
                  See our{" "}
                  <NavLink to="/lawn-care-website-examples/" className="text-primary underline hover:no-underline">
                    lawn care website examples
                  </NavLink>{" "}
                  and{" "}
                  <NavLink to="/service-pages-that-rank/" className="text-primary underline hover:no-underline">
                    service pages that rank
                  </NavLink>.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Agency vs Alternatives */}
        <section className="py-12 md:py-16 bg-accent/20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                Agency vs. Other Options
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4">Option</th>
                      <th className="text-left py-3 px-4">Cost</th>
                      <th className="text-left py-3 px-4">Timeline</th>
                      <th className="text-left py-3 px-4">Support</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b bg-primary/5">
                      <td className="py-3 px-4 font-semibold">Niche Agency (Us)</td>
                      <td className="py-3 px-4">$200–$300/mo</td>
                      <td className="py-3 px-4">10–14 days</td>
                      <td className="py-3 px-4">Ongoing</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-3 px-4">General Agency</td>
                      <td className="py-3 px-4">$3,000–$10,000+</td>
                      <td className="py-3 px-4">4–8 weeks</td>
                      <td className="py-3 px-4">Limited</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-3 px-4">Freelancer</td>
                      <td className="py-3 px-4">$1,000–$5,000</td>
                      <td className="py-3 px-4">2–6 weeks</td>
                      <td className="py-3 px-4">Variable</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-3 px-4">DIY Builder</td>
                      <td className="py-3 px-4">$0–$300/yr</td>
                      <td className="py-3 px-4">Weeks–Months</td>
                      <td className="py-3 px-4">Self-serve</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-center text-sm text-muted-foreground mt-4">
                Compare options:{" "}
                <NavLink to="/lawn-care-website-cost/" className="text-primary underline hover:no-underline">
                  lawn care website cost breakdown
                </NavLink>{" "}
                |{" "}
                <NavLink to="/websites/" className="text-primary underline hover:no-underline">
                  website subscription pricing
                </NavLink>
              </p>
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="py-12 md:py-16">
          <div className="container px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                Frequently Asked Questions About Lawn Care Web Design Agencies
              </h2>
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`}>
                    <AccordionTrigger className="text-left">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        {/* Related Guides */}
        <section className="py-12 md:py-16 bg-accent/20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                Related Guides
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <NavLink to="/lawn-care-website-design/" className="block p-4 rounded-lg border bg-background hover:border-primary transition-colors">
                  <h3 className="font-semibold mb-1">Lawn Care Website Design</h3>
                  <p className="text-sm text-muted-foreground">Complete guide to professional lawn care websites.</p>
                </NavLink>
                <NavLink to="/lawn-care-website-cost/" className="block p-4 rounded-lg border bg-background hover:border-primary transition-colors">
                  <h3 className="font-semibold mb-1">Lawn Care Website Cost</h3>
                  <p className="text-sm text-muted-foreground">Compare pricing across all options.</p>
                </NavLink>
                <NavLink to="/lawn-care-website-seo/" className="block p-4 rounded-lg border bg-background hover:border-primary transition-colors">
                  <h3 className="font-semibold mb-1">Lawn Care Website SEO</h3>
                  <p className="text-sm text-muted-foreground">SEO fundamentals for lawn care sites.</p>
                </NavLink>
                <NavLink to="/resources/" className="block p-4 rounded-lg border bg-background hover:border-primary transition-colors">
                  <h3 className="font-semibold mb-1">All Resources</h3>
                  <p className="text-sm text-muted-foreground">Browse all guides and resources.</p>
                </NavLink>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section id="schedule-call" className="py-16 md:py-20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto text-center space-y-6">
              <h2 className="text-3xl md:text-4xl font-bold">
                Ready to Work With a Lawn Care Web Design Agency?
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Let's discuss your lawn care business and see if we're a fit.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <NavLink to="/contact#get-started">
                  <Button size="lg">
                    <Phone className="mr-2 h-5 w-5" />
                    Get Started
                  </Button>
                </NavLink>
                <NavLink to="/websites/">
                  <Button size="lg" variant="outline">
                    View Website Subscription Pricing
                  </Button>
                </NavLink>
              </div>
            </div>
            <div className="max-w-2xl mx-auto mt-10">
              <CalendlyEmbed />
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default LawnCareWebDesignAgency;
